import {
  type ProfileTemplate,
  ProfileTemplatePublishStatus,
  type ProfileTemplateRevisionSummary,
  ProfileTemplatesApi,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Eye, History, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { IPhonePreview } from '@/core/components/ui/iphone-preview';
import { ensureProfileModuleEntryIds } from '@/core/lib/profile-module-entry-policies';
import { templateTextFieldToString } from '@/core/lib/profile-template-text-field';
import { parseTemplateContent } from '@/core/lib/profile-template-content';
import { extractApiErrorMessage } from '@/core/lib/profile-template-editor-snapshot';

const INITIAL_VISIBLE_COUNT = 6;

export type TemplateRevisionHistoryDict = {
  title: string;
  description: string;
  empty: string;
  loadFailed: string;
  restore: string;
  restoring: string;
  restoreSuccess: string;
  restoreError: string;
  restoreConfirmTitle: string;
  restoreConfirmDescription: string;
  restoreConfirm: string;
  restoreCancel: string;
  currentLive: string;
  revisionLabel: string;
  publishedAt: string;
  readOnlyHint: string;
  preview: string;
  previewTitle: string;
  previewLoadFailed: string;
  previewClose: string;
  showOlder: string;
  showLess: string;
  changedFromPrevious: string;
  initialVersion: string;
  teamsCount: string;
  teamsCountOne: string;
  cappedHint: string;
};

type TemplateRevisionHistoryProps = {
  workspaceId: string;
  templateId: string;
  workspaceUsername?: string | null;
  previewSampleName: string;
  previewSampleUsername: string;
  getFileUrl: (path: string) => string;
  canManage: boolean;
  publishStatus: ProfileTemplatePublishStatus;
  liveRevision: number | null;
  onRestored: (template: ProfileTemplate) => void;
  dict: TemplateRevisionHistoryDict;
};

type RevisionDetail = {
  revision: number;
  name: string;
  description?: string;
  teamIds: string[];
  content: ReturnType<typeof parseTemplateContent>;
};

function formatPublishedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function getChangeLabel(
  item: ProfileTemplateRevisionSummary,
  older: ProfileTemplateRevisionSummary | undefined,
  dict: TemplateRevisionHistoryDict,
): string | null {
  if (!older) return dict.initialVersion;
  if (item.contentSignature !== older.contentSignature) {
    return dict.changedFromPrevious.replace(
      '{revision}',
      String(older.revision),
    );
  }
  return null;
}

function buildPreviewProfileData(
  detail: RevisionDetail,
  previewSampleName: string,
) {
  const content = detail.content;
  const modules = ensureProfileModuleEntryIds(content.modules ?? []);
  const showProfileImage = content.showProfileImage ?? true;
  const showUp2promoBranding = content.showUp2promoBranding ?? true;

  return {
    id: `revision-preview-${detail.revision}`,
    title:
      templateTextFieldToString(content.title)?.trim() || previewSampleName,
    jobTitle: templateTextFieldToString(content.jobTitle) ?? null,
    company: templateTextFieldToString(content.company) ?? null,
    location: templateTextFieldToString(content.location) || null,
    profileImage:
      showProfileImage && content.profileImage?.path
        ? { path: content.profileImage.path }
        : null,
    showProfileImage,
    showUp2promoBranding,
    ownerPhoto: null,
    workspacePhoto: null,
    modules,
    styling: content.styling ?? {},
    leadForm: content.leadForm
      ? {
          enabled: content.leadForm.enabled ?? false,
          visibilityMode: (content.leadForm.visibilityMode ?? 'off') as
            | 'off'
            | 'manual'
            | 'auto_open',
          fields: content.leadForm.fields ?? [],
        }
      : {
          enabled: false,
          visibilityMode: 'off' as const,
          fields: [],
        },
  };
}

export function TemplateRevisionHistory({
  workspaceId,
  templateId,
  workspaceUsername,
  previewSampleName,
  previewSampleUsername,
  getFileUrl,
  canManage,
  publishStatus,
  liveRevision,
  onRestored,
  dict,
}: TemplateRevisionHistoryProps) {
  const [revisions, setRevisions] = useState<ProfileTemplateRevisionSummary[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [restoreTarget, setRestoreTarget] =
    useState<ProfileTemplateRevisionSummary | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDetail, setPreviewDetail] = useState<RevisionDetail | null>(
    null,
  );

  const syncLocked = publishStatus === ProfileTemplatePublishStatus.SYNCING;

  const loadRevisions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ProfileTemplatesApi.listRevisions({
        path: { workspaceId, templateId },
      });
      setRevisions(res.data ?? []);
    } catch (err) {
      console.error(err);
      toast.error(dict.loadFailed);
      setRevisions([]);
    } finally {
      setLoading(false);
    }
  }, [dict.loadFailed, templateId, workspaceId]);

  useEffect(() => {
    void (async () => {
      await loadRevisions();
    })();
  }, [loadRevisions]);

  const visibleRevisions = useMemo(() => {
    if (showAll || revisions.length <= INITIAL_VISIBLE_COUNT) {
      return revisions;
    }
    return revisions.slice(0, INITIAL_VISIBLE_COUNT);
  }, [revisions, showAll]);

  const hiddenCount = Math.max(0, revisions.length - INITIAL_VISIBLE_COUNT);

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const res = await ProfileTemplatesApi.restoreRevision({
        path: {
          workspaceId,
          templateId,
          revision: restoreTarget.revision,
        },
      });
      const restored = res.data as ProfileTemplate | undefined;
      if (!restored) throw new Error('empty response');
      onRestored(restored);
      toast.success(dict.restoreSuccess);
      setRestoreTarget(null);
    } catch (err) {
      console.error(err);
      toast.error(extractApiErrorMessage(err, dict.restoreError));
    } finally {
      setRestoring(false);
    }
  };

  const openPreview = async (item: ProfileTemplateRevisionSummary) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewDetail(null);
    try {
      const res = await ProfileTemplatesApi.getRevision({
        path: {
          workspaceId,
          templateId,
          revision: item.revision,
        },
      });
      const raw = res.data as
        | (ProfileTemplateRevisionSummary & {
            content?: unknown;
            description?: string;
          })
        | undefined;
      if (!raw) throw new Error('empty response');
      setPreviewDetail({
        revision: raw.revision,
        name: raw.name,
        description: raw.description,
        teamIds: raw.teamIds ?? [],
        content: parseTemplateContent((raw as { content?: unknown }).content),
      });
    } catch (err) {
      console.error(err);
      toast.error(dict.previewLoadFailed);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const previewProfileData = useMemo(
    () =>
      previewDetail
        ? buildPreviewProfileData(previewDetail, previewSampleName)
        : null,
    [previewDetail, previewSampleName],
  );

  return (
    <>
      <Card className="min-w-0 w-full">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-2.5">
            <History className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 space-y-1">
              <CardTitle className="text-base">{dict.title}</CardTitle>
              <CardDescription>{dict.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : revisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{dict.empty}</p>
          ) : (
            <>
              <ul className="divide-y rounded-lg border">
                {visibleRevisions.map((item) => {
                  const globalIndex = revisions.findIndex(
                    (r) => r.id === item.id,
                  );
                  const olderRevision = revisions[globalIndex + 1];
                  const isLive =
                    liveRevision != null && item.revision === liveRevision;
                  const changeLabel = getChangeLabel(item, olderRevision, dict);
                  const canRestore = canManage && !syncLocked && !isLive;

                  return (
                    <li
                      key={item.id}
                      className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <button
                        type="button"
                        className="min-w-0 flex-1 space-y-1 rounded-md text-left transition-colors hover:bg-muted/40 -m-1 p-1"
                        onClick={() => void openPreview(item)}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">
                            {dict.revisionLabel.replace(
                              '{revision}',
                              String(item.revision),
                            )}
                          </span>
                          {isLive ? (
                            <Badge variant="outline" className="text-xs">
                              {dict.currentLive}
                            </Badge>
                          ) : null}
                          {changeLabel ? (
                            <Badge
                              variant="secondary"
                              className="text-xs font-normal"
                            >
                              {changeLabel}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="truncate text-sm text-foreground">
                          {item.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {dict.publishedAt.replace(
                            '{date}',
                            formatPublishedAt(item.publishedAt),
                          )}
                          {' · '}
                          {(item.teamIds.length === 1
                            ? dict.teamsCountOne
                            : dict.teamsCount
                          ).replace('{count}', String(item.teamIds.length))}
                        </p>
                      </button>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void openPreview(item)}
                        >
                          <Eye className="mr-1.5 size-3.5" />
                          {dict.preview}
                        </Button>
                        {canRestore ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setRestoreTarget(item)}
                          >
                            <RotateCcw className="mr-1.5 size-3.5" />
                            {dict.restore}
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {hiddenCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowAll((value) => !value)}
                >
                  {showAll
                    ? dict.showLess
                    : dict.showOlder.replace('{count}', String(hiddenCount))}
                </Button>
              ) : null}

              {revisions.length >= 50 ? (
                <p className="text-xs text-muted-foreground">
                  {dict.cappedHint}
                </p>
              ) : null}
            </>
          )}

          {!canManage ? (
            <p className="text-xs text-muted-foreground">{dict.readOnlyHint}</p>
          ) : null}
        </CardContent>
      </Card>

      <ResponsiveDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <ResponsiveDialogContent
          scrollable
          className="max-w-4xl gap-0 overflow-hidden p-0 sm:max-h-[92vh]"
        >
          <ResponsiveDialogHeader className="border-b px-4 py-3 sm:px-6">
            <ResponsiveDialogTitle>
              {previewDetail
                ? dict.previewTitle.replace(
                    '{revision}',
                    String(previewDetail.revision),
                  )
                : dict.preview}
            </ResponsiveDialogTitle>
            {previewDetail?.description ? (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {previewDetail.description}
              </p>
            ) : null}
          </ResponsiveDialogHeader>
          <div className="flex min-h-[320px] flex-col items-center justify-center p-4 sm:p-6">
            {previewLoading ? (
              <Skeleton className="h-[520px] w-full max-w-sm rounded-[2rem]" />
            ) : previewProfileData ? (
              <IPhonePreview
                username={previewSampleUsername}
                workspaceId={workspaceId}
                workspaceUsername={workspaceUsername ?? null}
                profileData={previewProfileData}
                getFileUrl={getFileUrl}
                className="w-full max-w-sm"
              />
            ) : null}
          </div>
          <div className="flex justify-end border-t px-4 py-3 sm:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPreviewOpen(false)}
            >
              {dict.previewClose}
            </Button>
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <ConfirmDialog
        open={restoreTarget != null}
        onOpenChange={(open) => {
          if (!open && !restoring) setRestoreTarget(null);
        }}
        title={dict.restoreConfirmTitle}
        description={dict.restoreConfirmDescription.replace(
          '{revision}',
          restoreTarget ? String(restoreTarget.revision) : '',
        )}
        confirmLabel={restoring ? dict.restoring : dict.restoreConfirm}
        cancelLabel={dict.restoreCancel}
        onConfirm={() => void handleRestore()}
      />
    </>
  );
}
